import React, { useState, useEffect } from 'react';
import { getUserProfile, getBorrowerReviewForUser } from '../services/api';
import {
    Container,
    Typography,
    Grid,
    Box,
    Avatar,
    Rating,
    Paper,
    CircularProgress,
    Card,
    CardContent,
    CardMedia,
    CardActionArea,
    Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, Link } from 'react-router-dom';
import {
    Star as StarIcon,
    LocationOn as LocationIcon,
    Shield as ShieldIcon,
    VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';

const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    maxWidth: '1200px',
}));

const ProfileHeader = styled(Paper)(({ theme }) => ({
    position: 'relative',
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
}));

const LargeAvatar = styled(Avatar)(({ theme }) => ({
    width: theme.spacing(15),
    height: theme.spacing(15),
    border: `4px solid ${theme.palette.background.paper}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
}));

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    },
}));

const StatBox = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    textAlign: 'center',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
}));

const ReviewCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-2px)',
    },
}));

const StyledSection = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
}));

const VerificationBadge = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    borderRadius: '16px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    gap: '6px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    '& .MuiSvgIcon-root': {
        fontSize: '1.2rem',
    },
}));

const ShowMoreButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(2),
    borderRadius: '8px',
    padding: '8px 24px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
    },
}));

function UserProfile() {
    const { userId } = useParams();
    const [profileData, setProfileData] = useState(null);
    const [borrowerReviews, setBorrowerReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAllListings, setShowAllListings] = useState(false);
    const [loadingBorrowerReviews, setLoadingBorrowerReviews] = useState(true);

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                const [profileResponse, borrowerReviewsResponse] = await Promise.all([
                    getUserProfile(userId),
                    getBorrowerReviewForUser(userId)
                ]);

                if (profileResponse && profileResponse.user_info) {
                    setProfileData(profileResponse);
                } else {
                    setError('Invalid profile response format');
                }

                if (borrowerReviewsResponse.data) {
                    setBorrowerReviews(borrowerReviewsResponse.data);
                }
            } catch (err) {
                setError(err.message || 'Error fetching profile data');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
                setLoadingBorrowerReviews(false);
            }
        };

        if (userId && userId !== 'undefined') {
            fetchProfileData();
        } else {
            setError('Invalid user ID');
            setLoading(false);
        }
    }, [userId]);

    const formatPrice = (price) => {
        if (typeof price === 'number') {
            return price.toFixed(2);
        } else if (typeof price === 'string' && !isNaN(price)) {
            return parseFloat(price).toFixed(2);
        }
        return '0.00';
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !profileData?.user_info) {
        return (
            <StyledContainer>
                <Typography color="error" align="center" variant="h6" sx={{ mt: 4 }}>
                    {error || 'No profile data found'}
                </Typography>
            </StyledContainer>
        );
    }

    const { user_info, listings = [], reviews = [] } = profileData;
    const displayedListings = showAllListings ? listings : listings.slice(0, 3);

    // Check if user has completed transactions
    const hasCompletedTransactions = user_info.completed_transactions && user_info.completed_transactions > 0;

    return (
        <StyledContainer>
            <ProfileHeader elevation={0}>
                <Grid container spacing={4} alignItems="center">
                    <Grid item>
                        <LargeAvatar
                            src={user_info.profile_picture}
                            alt={user_info.first_name}
                        >
                            {user_info.first_name?.[0]}
                        </LargeAvatar>
                    </Grid>
                    <Grid item xs={12} sm>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                                {`${user_info.first_name} ${user_info.last_name}`}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={3} mb={2}>
                            <Box display="flex" alignItems="center">
                                <StarIcon sx={{ color: '#FFB400', marginRight: 0.5 }} />
                                <Typography variant="body1">
                                    {parseFloat(user_info.rating_average || '0.0').toFixed(1)}
                                    <Typography component="span" color="text.secondary" sx={{ ml: 0.5 }}>
                                        ({Array.isArray(reviews) ? reviews.length : 0} reviews)
                                    </Typography>
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <LocationIcon color="action" sx={{ marginRight: 0.5 }} />
                                <Typography variant="body1">
                                    {user_info.zip_code || 'Location not specified'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box display="flex" gap={2}>
                            {user_info.persona_status === 'approved' && (
                                <VerificationBadge>
                                    <ShieldIcon />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Identity Verified
                                    </Typography>
                                </VerificationBadge>
                            )}
                            {user_info.stripe_connected_account_id && (
                                <VerificationBadge>
                                    <VerifiedUserIcon />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Payment Verified
                                    </Typography>
                                </VerificationBadge>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </ProfileHeader>

            <Grid container spacing={3} mb={4}>
                {/* Use different grid sizes based on whether completed transactions exist */}
                <Grid item xs={12} sm={hasCompletedTransactions ? 4 : 6}>
                    <StatBox elevation={0}>
                        <Typography variant="h3" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                            {Array.isArray(listings) ? listings.length : 0}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            Active Listings
                        </Typography>
                    </StatBox>
                </Grid>
                <Grid item xs={12} sm={hasCompletedTransactions ? 4 : 6}>
                    <StatBox elevation={0}>
                        <Typography variant="h3" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                            {Array.isArray(reviews) ? reviews.length : 0}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            Reviews Received
                        </Typography>
                    </StatBox>
                </Grid>
                {/* Only render the completed transactions box if there are transactions */}
                {hasCompletedTransactions && (
                    <Grid item xs={12} sm={4}>
                        <StatBox elevation={0}>
                            <Typography variant="h3" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                                {user_info.completed_transactions}
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary">
                                Completed Transactions
                            </Typography>
                        </StatBox>
                    </Grid>
                )}
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <StyledSection elevation={0}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                            Active Listings ({Array.isArray(listings) ? listings.length : 0})
                        </Typography>
                        <Grid container spacing={2}>
                            {Array.isArray(displayedListings) && displayedListings.length > 0 ? (
                                displayedListings.map((listing) => (
                                    <Grid item xs={12} sm={6} key={listing.id || Math.random()}>
                                        <StyledCard elevation={0}>
                                            <CardActionArea
                                                component={Link}
                                                to={`/listings/${listing.uuid || ''}`}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    height="200"
                                                    image={listing.image_url || '/placeholder-image.jpg'}
                                                    alt={listing.title || 'Listing image'}
                                                    sx={{
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                                <CardContent>
                                                    <Typography
                                                        gutterBottom
                                                        variant="h6"
                                                        component="h2"
                                                        noWrap
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        {listing.title || 'Untitled Listing'}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            mb: 2,
                                                            height: '40px',
                                                            lineHeight: '1.3',
                                                        }}
                                                    >
                                                        {listing.description || 'No description available'}
                                                    </Typography>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                                        <Typography
                                                            variant="h6"
                                                            color="primary"
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            ${formatPrice(listing.price)}
                                                            <Typography
                                                                component="span"
                                                                color="text.secondary"
                                                                variant="body2"
                                                            >
                                                                /day
                                                            </Typography>
                                                        </Typography>
                                                        {listing.average_rating > 0 && (
                                                            <Box display="flex" alignItems="center">
                                                                <StarIcon sx={{ color: '#FFB400', fontSize: '1.2rem', mr: 0.5 }} />
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {listing.average_rating.toFixed(1)}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </CardActionArea>
                                        </StyledCard>
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12}>
                                    <Typography color="text.secondary" align="center">
                                        No active listings
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                        {listings.length > 3 && (
                            <Box display="flex" justifyContent="center">
                                <ShowMoreButton
                                    onClick={() => setShowAllListings(!showAllListings)}
                                    variant="contained"
                                >
                                    {showAllListings ? 'Show Less' : 'Show More Listings'}
                                </ShowMoreButton>
                            </Box>
                        )}
                    </StyledSection>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <StyledSection elevation={0}>
                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                                    Lender Reviews ({Array.isArray(reviews) ? reviews.length : 0})
                                </Typography>
                                <Box sx={{ maxHeight: '600px', overflowY: 'auto', pr: 1 }}>
                                    <Grid container spacing={2}>
                                        {Array.isArray(reviews) && reviews.length > 0 ? (
                                            reviews.map((review) => (
                                                <Grid item xs={12} key={review.id || Math.random()}>
                                                    <ReviewCard elevation={0}>
                                                        <Box display="flex" alignItems="center" mb={1}>
                                                            <Avatar
                                                                src={review.reviewer.profile_picture}
                                                                alt={`${review.reviewer.first_name} ${review.reviewer.last_name}`}
                                                                sx={{ width: 32, height: 32, mr: 1 }}
                                                            >
                                                                {review.reviewer_name?.[0]}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                                                    {`${review.reviewer.first_name} ${review.reviewer.last_name}` || 'Anonymous'}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {review.created_at ? new Date(review.created_at).toLocaleDateString('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'long',
                                                                        day: 'numeric'
                                                                    }) : 'No date'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box display="flex" alignItems="center" mb={1}>
                                                            <Rating
                                                                value={typeof review.rating === 'number' ? review.rating : 0}
                                                                readOnly
                                                                size="small"
                                                                precision={0.5}
                                                                sx={{
                                                                    '& .MuiRating-iconFilled': {
                                                                        color: '#FFB400',
                                                                    },
                                                                }}
                                                            />
                                                            <Typography variant="body2" color="text.secondary" ml={1}>
                                                                {typeof review.rating === 'number' ? review.rating.toFixed(1) : '0.00'}
                                                            </Typography>
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.primary',
                                                                lineHeight: 1.6
                                                            }}
                                                        >
                                                            {review.comment || 'No comment provided'}
                                                        </Typography>
                                                    </ReviewCard>
                                                </Grid>
                                            ))
                                        ) : (
                                            <Grid item xs={12}>
                                                <Typography color="text.secondary" align="center">
                                                    No reviews yet
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            </StyledSection>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <StyledSection elevation={0}>
                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                                    Borrower Reviews ({borrowerReviews.length})
                                </Typography>
                                <Box sx={{ maxHeight: '600px', overflowY: 'auto', pr: 1 }}>
                                    <Grid container spacing={2}>
                                        {loadingBorrowerReviews ? (
                                            <Grid item xs={12}>
                                                <Box display="flex" justifyContent="center">
                                                    <CircularProgress size={24} />
                                                </Box>
                                            </Grid>
                                        ) : borrowerReviews.length > 0 ? (
                                            borrowerReviews.map((review) => (
                                                <Grid item xs={12} key={review.id || Math.random()}>
                                                    <ReviewCard elevation={0}>
                                                        <Box display="flex" alignItems="center" mb={1}>
                                                            <Avatar
                                                                src={review.reviewer?.profile_picture}
                                                                alt={`${review.reviewer?.first_name} ${review.reviewer?.last_name}`}
                                                                sx={{ width: 32, height: 32, mr: 1 }}
                                                            >
                                                                {review.reviewer?.first_name?.[0]}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                                                    {`${review.reviewer?.first_name} ${review.reviewer?.last_name}` || 'Anonymous'}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {new Date(review.created_at).toLocaleDateString('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'long',
                                                                        day: 'numeric'
                                                                    })}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box display="flex" alignItems="center" mb={1}>
                                                            <Rating
                                                                value={review.rating}
                                                                readOnly
                                                                size="small"
                                                                precision={0.5}
                                                                sx={{ '& .MuiRating-iconFilled': { color: '#FFB400' } }}
                                                            />
                                                            <Typography variant="body2" color="text.secondary" ml={1}>
                                                                {review.rating.toFixed(1)}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                                                            {review.comment || 'No comment provided'}
                                                        </Typography>
                                                    </ReviewCard>
                                                </Grid>
                                            ))
                                        ) : (
                                            <Grid item xs={12}>
                                                <Typography color="text.secondary" align="center">
                                                    No borrower reviews yet
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            </StyledSection>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </StyledContainer>
    );
}

export default UserProfile;